import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { backtestRuns as api } from "@/lib/api/backtest-runs";
import type { BacktestRunInput } from "@/lib/api/backtest-runs";
import type { BacktestRun, BacktestRunSummary } from "@/types/api";
import { useAuth } from "./useAuth";

export type BacktestRunsStatus = "idle" | "loading" | "ready" | "error";

export interface BacktestRunsContextValue {
  status: BacktestRunsStatus;
  runs: readonly BacktestRunSummary[];
  byTemplate: Map<
    string,
    BacktestRunSummary & {
      total_pnl: number;
      total_trades: number;
      win_rate: number;
      sharpe: number;
    }
  >;
  error: Error | null;
  reload: () => Promise<void>;
  create: (input: BacktestRunInput) => Promise<BacktestRun>;
  remove: (id: number | string) => Promise<void>;
  fetchOne: (id: number | string) => Promise<BacktestRun>;
}

const BacktestRunsContext = createContext<BacktestRunsContextValue>(null!);

export function useBacktestRuns(): BacktestRunsContextValue {
  return useContext(BacktestRunsContext);
}

const POLL_INTERVAL_MS = 2000;

function useBacktestRunsProvider(): BacktestRunsContextValue {
  const { status: authStatus } = useAuth();
  const [status, setStatus] = useState<BacktestRunsStatus>("idle");
  const [runs, setRuns] = useState<readonly BacktestRunSummary[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [pollTick, setPollTick] = useState(0);

  const reload = useCallback(
    () =>
      api
        .listBacktestRuns()
        .then(({ backtest_runs }) => {
          setRuns(backtest_runs);
          setStatus("ready");
          setError(null);
        })
        .catch((e: Error) => {
          setError(e);
          setStatus("error");
        }),
    [],
  );

  useEffect(() => {
    if (authStatus !== "authenticated") return;
    const hasUnfinished = runs.some((r) => r.finished_at === null);
    if (!hasUnfinished) return;
    pollTimer.current = setTimeout(() => {
      void reload().finally(() => setPollTick((n) => n + 1));
    }, POLL_INTERVAL_MS);
    return () => {
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
  }, [runs, pollTick, authStatus, reload]);

  useEffect(() => {
    if (authStatus !== "authenticated") return;
    void reload();
  }, [authStatus, reload]);

  const create = useCallback(
    async (input: BacktestRunInput): Promise<BacktestRun> => {
      const { backtest_run } = await api.createBacktestRun(input);
      setRuns((prev) => [backtest_run, ...prev]);
      return backtest_run;
    },
    [],
  );

  const remove = useCallback(async (id: number | string) => {
    await api.deleteBacktestRun(id);
    setRuns((prev) => prev.filter((r) => String(r.id) !== String(id)));
  }, []);

  const fetchOne = useCallback(async (id: number | string) => {
    const { backtest_run } = await api.getBacktestRun(id);
    return backtest_run;
  }, []);

  const byTemplate = useMemo(() => {
    const map = new Map<
      string,
      BacktestRunSummary & {
        total_pnl: number;
        total_trades: number;
        win_rate: number;
        sharpe: number;
      }
    >();
    for (const r of runs) {
      if (r.saved_strategy_id && r.finished_at && !r.results_summary?.error) {
        const s = r.results_summary;
        const key = String(r.saved_strategy_id);
        const held = map.get(key);
        if (held && (held.started_at ?? "") >= (r.started_at ?? "")) continue;
        map.set(key, {
          ...r,
          total_pnl: s?.total_pnl ?? 0,
          total_trades: s?.total_trades ?? 0,
          win_rate: s?.win_rate ?? 0,
          sharpe: s?.sharpe ?? 0,
        });
      }
    }
    return map;
  }, [runs]);

  return useMemo(
    () => ({
      status,
      runs,
      byTemplate,
      error,
      reload,
      create,
      remove,
      fetchOne,
    }),
    [status, runs, byTemplate, error, reload, create, remove, fetchOne],
  );
}

export function BacktestRunsProvider({ children }: { children: ReactNode }) {
  const value = useBacktestRunsProvider();
  return (
    <BacktestRunsContext.Provider value={value}>
      {children}
    </BacktestRunsContext.Provider>
  );
}
